import React from 'react';
import {
  BasicConfig,
  Config,
  Conjunctions,
  Fields,
  Funcs,
  LocaleSettings,
  OperatorProximity,
  // types:
  Operators,
  Settings,
  Types,
  Widgets
} from 'react-awesome-query-builder';
import { QueryBuilderState } from './query-builder-state';
import BooleanWidget from './widgets/BooleanWidget';
import ButtonGroupWidget from './widgets/ButtonGroupWidget';
import ButtonWidget from './widgets/ButtonWidget';
import ConjunctionSelect from './widgets/ConjunctionSelect';
import DateWidget from './widgets/DateWidget';
import FieldSelect from './widgets/FieldSelect';
import ListWidget from './widgets/ListWidget';
import MultiListWidget from './widgets/MultiListWidget';
import NUmberWidget from './widgets/NumberWidget';
import OperatorSelect from './widgets/OperatorSelect';
import TextWidget from './widgets/TextWidget';
import TimeWidget from './widgets/TimeWidget';

const getFieldDefinitionByType = (type: string): any => {
  switch (type) {
    case 'boolean':
      return {
        type: 'textBoolean',
        defaultValue: 'True',
        valueSources: ['value']
      };
    case 'date':
      return {
        type: 'date',
        excludeOperators: ['is_empty', 'is_not_empty'],
        fieldSettings: {
          validateValue: (val: string) => {
            return val.length === 10;
          }
        },
        valueSources: ['value']
      };
    case 'time':
      return {
        type: 'time',
        excludeOperators: ['is_empty', 'is_not_empty'],
        valueSources: ['value']
      };
    case 'number':
      return {
        type: 'number',
        excludeOperators: ['is_empty', 'is_not_empty'],
        fieldSettings: {
          validateValue: (val: string) => {
            return val.length > 0 && /^[+-]?\d*(?:[.,]\d*)?$/.test(val);
          }
        },

        valueSources: ['value']
      };
    case 'text':
    default:
      return {
        type: 'text',
        defaultOperator: 'like',
        excludeOperators: ['proximity', 'not_like', 'not_equal'],
        fieldSettings: {
          validateValue: (val: string) => {
            return val.length > 0;
          }
        },
        valueSources: ['value']
      };
  }
};

const getAttributeFields = (attributeInfos: Array<any>): Fields =>
  attributeInfos.reduce((curry: any, current: any) => {
    const key =
      current.attribute === 'name'
        ? current.attribute
        : `attributes.${current.attribute.split('.').join('##')}.value`;

    // eslint-disable-next-line no-param-reassign
    curry[key] = {
      label: current.displayName,
      ...getFieldDefinitionByType(current.type)
    };

    return curry;
  }, {} as Record<string, any>);

interface AttributeInfos {
  activeAttributes: Array<any>;
  attributes: Array<any>;
}

const getDefaultFields = (): Fields => ({
  ...getAttributeFields([{ attribute: 'name', displayName: 'Name' }]),
  roles: {
    label: 'Roles',
    type: 'select',
    defaultOperator: 'contains_any_of',
    operators: ['contains_any_of', 'contains_value', 'contains_all_of'],
    fieldSettings: {
      allowCustomValues: true,
      listValues: []
    },
    valueSources: ['value']
  }
});

const getFields = (activeAttributes: any, attributes: any): Fields => ({
  ...getDefaultFields(),
  ...getAttributeFields(activeAttributes),
  ...getAttributeFields(attributes)
});

export default (
  dataFetch: (fieldName: string) => Promise<QueryBuilderState>,
  attributeInfos: AttributeInfos
) => {
  const InitialConfig = BasicConfig;

  const conjunctions: Conjunctions = {
    ...InitialConfig.conjunctions
  };

  const proximity: OperatorProximity = {
    ...InitialConfig.operators.proximity,
    valueLabels: [
      { label: 'Word 1', placeholder: 'Enter first word' },
      { label: 'Word 2', placeholder: 'Enter second word' }
    ],
    textSeparators: [
      // 'Word 1',
      // 'Word 2'
    ],
    options: {
      ...InitialConfig.operators.proximity.options,
      optionLabel: 'Near', // label on top of "near" selectbox (for config.settings.showLabels==true)
      optionTextBefore: 'Near', // label before "near" selectbox (for config.settings.showLabels==false)
      optionPlaceholder: 'Select words between', // placeholder for "near" selectbox
      minProximity: 2,
      maxProximity: 10,
      defaults: {
        proximity: 2
      },
      customProps: {}
    }
  };

  const operators: Operators = {
    ...InitialConfig.operators,
    proximity,
    starts_with: {
      ...InitialConfig.operators.starts_with,
      jsonLogic: 'startsWith' // custom operation, add this to jsonLogic http://jsonlogic.com/add_operation.html
    },
    ends_with: {
      ...InitialConfig.operators.ends_with,
      jsonLogic: 'endsWith' // custom operation, add this to jsonLogic http://jsonlogic.com/add_operation.html
    },
    equal: {
      ...InitialConfig.operators.equal,
      label: 'Equal'
    },
    not_equal: {
      ...InitialConfig.operators.not_equal,
      label: 'Not equal'
    },
    like: {
      ...InitialConfig.operators.like,
      label: 'Contain'
    },
    not_like: {
      ...InitialConfig.operators.not_like,
      label: 'Not contain'
    },
    between: {
      ...InitialConfig.operators.between,
      valueLabels: ['Value from', 'Value to'],
      textSeparators: ['from', 'to']
    },
    contains_any_of: {
      ...InitialConfig.operators.select_any_in,
      label: 'Contains any of',
      jsonLogic: 'containsAny'

      // these do not work, maybe fix at some point to get rid of custom jsonlogic operator?
      /*
      jsonLogic: (field, op, vals) => ({
        in: [vals, field]
      })
      jsonLogic: (field, op, vals) => ({
        // it's not "equals", but "includes" operator - just for example
        all: [vals, { in: [{ var: '' }, field] }]
      })
      */
    },
    contains_value: {
      ...InitialConfig.operators.select_any_in,
      label: 'Contains',
      jsonLogic: 'containsValue'
    },
    contains_all_of: {
      ...InitialConfig.operators.select_any_in,
      label: 'Contains all of',
      jsonLogic: 'containsAll'
    }
  };

  const widgets: Widgets = {
    ...InitialConfig.widgets,
    // examples of  overriding
    text: {
      ...InitialConfig.widgets.text,
      factory: (props: any) => <TextWidget {...props} />
    },
    number: {
      ...InitialConfig.widgets.text, //  text suits better than number
      factory: (props: any) => <NUmberWidget {...props} />
    },
    select: {
      ...InitialConfig.widgets.select,
      customProps: { dataFetch },
      factory: (props: any) => <ListWidget {...props} />
    },
    multiselect: {
      ...InitialConfig.widgets.multiselect,
      customProps: { dataFetch },
      factory: (props: any) => <MultiListWidget {...props} />
    },
    slider: {
      ...InitialConfig.widgets.slider,
      customProps: {
        width: '300px'
      }
    },
    rangeslider: {
      ...InitialConfig.widgets.rangeslider,
      customProps: {
        width: '300px'
      }
    },
    date: {
      ...InitialConfig.widgets.date,
      dateFormat: 'DD.MM.YYYY',
      valueFormat: 'YYYY-MM-DD',
      factory: (props: any) => <DateWidget {...props} />
    },
    time: {
      ...InitialConfig.widgets.time,
      timeFormat: 'HH:mm',
      valueFormat: 'HH:mm:ss',
      factory: (props: any) => <TimeWidget {...props} />
    },
    datetime: {
      ...InitialConfig.widgets.datetime,
      timeFormat: 'HH:mm',
      dateFormat: 'DD.MM.YYYY',
      valueFormat: 'YYYY-MM-DD HH:mm:ss'
    },
    func: {
      ...InitialConfig.widgets.func,
      customProps: {
        showSearch: true
      }
    },
    treeselect: {
      ...InitialConfig.widgets.treeselect,
      customProps: {
        showSearch: true
      }
    },
    textBoolean: {
      ...InitialConfig.widgets.text,
      factory: (props: any) => <BooleanWidget {...props} />
    }
  };

  const types: Types = {
    ...InitialConfig.types,
    textBoolean: {
      defaultOperator: 'equal',
      widgets: {
        textBoolean: { operators: ['equal', 'not_equal'] },
        field: { operators: ['equal', 'not_equal'] },
        func: {}
      },
      mainWidget: 'textBoolean',
      valueSources: ['value', 'field', 'func'],
      operators: ['equal', 'not_equal']
    } as any,
    select: {
      ...InitialConfig.types.select,
      widgets: {
        select: {
          operators: ['select_equals', 'select_not_equals', 'contains_value'],
          widgetProps: {
            customProps: {
              showSearch: true
            }
          }
        },
        multiselect: {
          operators: ['select_any_in', 'select_not_any_in', 'contains_any_of', 'contains_all_of']
        }
      }
    }
  };

  const localeSettings: LocaleSettings = {
    valueLabel: 'Value',
    valuePlaceholder: 'Value',
    fieldLabel: 'Field',
    operatorLabel: 'Operator',
    funcLabel: 'Function',
    fieldPlaceholder: 'Select field',
    funcPlaceholder: 'Select function',
    operatorPlaceholder: 'Select operator',
    deleteLabel: 'Delete',
    addGroupLabel: 'Add group',
    addRuleLabel: 'Add rule',
    delGroupLabel: 'Delete group',
    notLabel: 'Not',
    valueSourcesPopupTitle: 'Select value source'
  };

  const settings: Settings = {
    ...InitialConfig.settings,
    ...localeSettings,
    // canReorder: true,
    // canRegroup: true,
    showNot: false,
    // showLabels: true,
    maxNesting: 3,
    canLeaveEmptyGroup: false, // after deletion
    showErrorMessage: false,
    fieldSeparator: '-',
    renderField: (props: any) => <FieldSelect {...props} />,
    renderOperator: (props: any) => <OperatorSelect {...props} />,
    renderConjs: (props: any) => <ConjunctionSelect {...props} />,
    renderButton: (props: any) => <ButtonWidget {...props} />,
    renderButtonGroup: (props: any) => <ButtonGroupWidget {...props} />
    // renderFunc: (props) => <FieldSelect {...props} />,
    // maxNumberOfRules: 10 // number of rules can be added to the query builder
  };

  const funcs: Funcs = {};

  const config: Config = {
    conjunctions,
    operators,
    widgets,
    types,
    settings,
    fields: getFields(attributeInfos.activeAttributes, attributeInfos.attributes),
    funcs
  };

  return config;
};
